/**
 * Standings - full table with W/L/T, win%, PF, PA, streak, last 5, playoff seed line.
 * Phase 1.5: division-grouped tables when divisions > 0; PLAYOFFS / SEASON_COMPLETE CTAs.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Trophy, Crown, ChevronRight } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { authHeaders } from "../contexts/AuthContext";
import SeasonShell from "../components/season/SeasonShell";
import ManagerName from "../components/ManagerName";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Standings() {
  const { id: leagueId } = useParams();
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [leagueMeta, setLeagueMeta] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    setLoadError(null);
    axios.get(`${API}/standings/${leagueId}`, { headers: authHeaders() })
      .then((r) => setData(r.data))
      .catch((e) => setLoadError(e?.response?.data?.detail || e?.message || "Failed to load standings"));
    // Optional read - degrades gracefully
    axios.get(`${API}/leagues/${leagueId}`, { headers: authHeaders() })
      .then((r) => setLeagueMeta(r.data))
      .catch((_e) => setLeagueMeta(null));
  }, [leagueId]);

  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";
  const fg = isDark ? "text-[#F5F5F5]" : "text-[#111111]";
  const cardBg = isDark ? "bg-[#1C1C1C]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";
  const rowAlt = isDark ? "bg-[#252525]" : "bg-[#F5F5F5]";

  if (!data) {
    if (loadError) {
      return (
        <SeasonShell>
          <div className="py-20 text-center" data-testid="standings-load-error">
            <p className="font-display font-black uppercase text-xl text-[#FF4444] mb-2">Could not load standings</p>
            <p className={`font-body text-sm ${muted} mb-4`}>{loadError}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-white text-sm font-display font-bold uppercase tracking-wide"
              data-testid="standings-retry"
            >Retry</button>
          </div>
        </SeasonShell>
      );
    }
    return <SeasonShell><div className="py-20 text-center"><span className="font-display font-black uppercase text-2xl text-[#FF6B00]">Loading standings…</span></div></SeasonShell>;
  }

  return (
    <SeasonShell>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">Standings</h1>
        {leagueMeta?.status === "PLAYOFFS" && (
          <Link
            to={`/league/${leagueId}/playoffs`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FF6B00] hover:bg-[#FF8533] text-white font-display font-black uppercase text-xs tracking-wide"
            data-testid="standings-bracket-cta"
          >
            <Trophy size={14} /> View Bracket
          </Link>
        )}
        {leagueMeta?.status === "SEASON_COMPLETE" && (
          <Link
            to={`/league/${leagueId}/recap`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FFD700]/20 hover:bg-[#FFD700]/30 border border-[#FFD700]/60 text-[#FFD700] font-display font-black uppercase text-xs tracking-wide"
            data-testid="standings-recap-cta"
          >
            <Crown size={14} /> Season Recap
          </Link>
        )}
      </div>

      {/* Division-grouped standings (Phase 1.5 §4.5) */}
      {data.divisions && Object.keys(data.divisions).length > 0 && (
        <div className="space-y-4 mb-6" data-testid="standings-divisions">
          {Object.entries(data.divisions).map(([divId, rows]) => (
            <div key={divId} className={`rounded-2xl ${cardBg} border ${cardBorder} overflow-hidden`}>
              <div className="px-4 py-3 bg-[#FF6B00]/10 border-b border-[#FF6B00]/30">
                <span className="font-display font-black uppercase tracking-wide text-sm text-[#FF6B00]">
                  {data.division_names?.[divId] || divId}
                </span>
              </div>
              <table className="w-full text-sm">
                <thead className={`text-[10px] uppercase tracking-widest ${muted}`}>
                  <tr className={rowAlt}>
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">Team</th>
                    <th className="px-3 py-2 text-right">W-L-T</th>
                    <th className="px-3 py-2 text-right">PF</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={r.team_id} className={idx % 2 === 1 ? rowAlt : ""} data-testid={`div-row-${divId}-${idx}`}>
                      <td className="px-3 py-2 font-display font-black">
                        {idx === 0 ? <span className="px-1.5 py-0.5 rounded bg-[#FF6B00]/30 text-[#FF6B00] text-[9px] font-bold">DIV LEADER</span> : `#${idx + 1}`}
                      </td>
                      <td className="px-3 py-2">
                        <Link to={`/league/${leagueId}/team/${r.team_id}`} className="font-display font-bold uppercase text-xs hover:text-[#FF6B00]">
                          <ManagerName name={r.team_name} isBot={r.is_bot} testId={`div-mgr-${r.team_id}`} />
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right">{r.wins}-{r.losses}-{r.ties}</td>
                      <td className="px-3 py-2 text-right font-display font-black">{r.points_for.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      <div className={`rounded-2xl ${cardBg} border ${cardBorder} overflow-hidden`} data-testid="standings-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className={`text-[10px] uppercase tracking-widest ${muted}`}>
              <tr className={`${rowAlt}`}>
                <th className="px-3 py-3 text-left">#</th>
                <th className="px-3 py-3 text-left">Team</th>
                <th className="px-3 py-3 text-right">W</th>
                <th className="px-3 py-3 text-right">L</th>
                <th className="px-3 py-3 text-right">T</th>
                <th className="px-3 py-3 text-right">Win %</th>
                <th className="px-3 py-3 text-right">PF</th>
                <th className="px-3 py-3 text-right">PA</th>
                <th className="px-3 py-3 text-right">Streak</th>
                <th className="px-3 py-3 text-right hidden md:table-cell">Last 5</th>
              </tr>
            </thead>
            <tbody>
              {data.standings.map((r, idx) => {
                const isLastPlayoff = idx === 5;
                return (
                  <tr
                    key={r.team_id}
                    className={`${idx % 2 === 1 ? rowAlt : ""} ${isLastPlayoff ? "border-b-2 border-dashed border-[#FF6B00]/60" : ""}`}
                    data-testid={`standings-row-${r.team_id}`}
                  >
                    <td className={`px-3 py-2.5 font-display font-black ${idx < 6 ? "text-[#00C853]" : muted}`}>{r.rank}</td>
                    <td className="px-3 py-2.5">
                      <Link to={`/league/${leagueId}/team/${r.team_id}`} className="font-display font-bold uppercase text-xs hover:text-[#FF6B00]">
                        <ManagerName name={r.team_name} isBot={r.is_bot} testId={`standings-mgr-${r.team_id}`} />
                      </Link>
                    </td>
                    <td className="px-3 py-2.5 text-right">{r.wins}</td>
                    <td className="px-3 py-2.5 text-right">{r.losses}</td>
                    <td className="px-3 py-2.5 text-right">{r.ties}</td>
                    <td className="px-3 py-2.5 text-right">{(r.win_pct * 100).toFixed(0)}%</td>
                    <td className="px-3 py-2.5 text-right font-display font-black">{r.points_for.toFixed(1)}</td>
                    <td className="px-3 py-2.5 text-right">{r.points_against.toFixed(1)}</td>
                    <td className="px-3 py-2.5 text-right">{r.streak}</td>
                    <td className="px-3 py-2.5 text-right hidden md:table-cell">
                      <div className="flex justify-end gap-1">
                        {(r.last_5 || []).map((res, i) => (
                          <span key={i} className={`inline-block w-4 h-4 rounded text-[9px] font-bold flex items-center justify-center ${
                            res === "W" ? "bg-[#00C853]/20 text-[#00C853]" :
                            res === "L" ? "bg-red-500/20 text-red-400" : "bg-[#FF6B00]/20 text-[#FF6B00]"
                          }`}>{res}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 px-2">
        <p className={`text-[10px] ${muted}`}>
          <strong>Tiebreakers:</strong> {data.tiebreaker_chain.join(" → ")}
        </p>
        <p className={`text-[10px] ${muted} mt-1`}>
          Dashed line = current playoff cut (top 6).
        </p>
      </div>
    </SeasonShell>
  );
}
